import React, { useEffect, useRef, useState } from "react";
import { Columns3, Save, Search, X } from "lucide-react";
import { DESINST, viewStatuses } from "./savViews";

const sel = "px-2 py-1.5 text-xs border border-norea-border rounded bg-white focus:ring-1 focus:ring-norea-primary outline-none";

function StatusChips({ statuses, value = [], onChange }) {
  const on = new Set(value);
  return (
    <div className="flex flex-wrap gap-1" data-testid="sav-filter-status">
      {statuses.map((s) => (
        <button key={s.id} onClick={() => onChange(on.has(s.id) ? value.filter((x) => x !== s.id) : [...value, s.id])} data-testid={`sav-filter-status-${s.id}`}
          className={`text-[11px] px-2 py-0.5 rounded-full border transition-colors ${on.has(s.id) ? "bg-norea-primary text-white border-norea-primary" : "bg-white border-norea-border text-norea-textMuted hover:text-norea-text"}`}>{s.label}</button>
      ))}
    </div>
  );
}

function ColumnsPicker({ all, value, onChange }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return undefined;
    const close = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, [open]);
  return (
    <div className="relative" ref={ref}>
      <button onClick={() => setOpen(!open)} className="btn-ghost text-xs" data-testid="sav-columns-btn"><Columns3 className="w-3.5 h-3.5" /> Colonnes ({value.length})</button>
      {open && (
        <div className="absolute right-0 z-30 mt-1 w-60 max-h-80 overflow-y-auto bg-white border border-norea-border rounded-md shadow-lg p-2 space-y-0.5" data-testid="sav-columns-menu">
          {all.map((c) => (
            <label key={c.id} className="flex items-center gap-2 text-xs px-1.5 py-1 rounded hover:bg-norea-surfaceHover cursor-pointer">
              <input type="checkbox" checked={value.includes(c.id)} onChange={(e) => onChange(e.target.checked ? [...value, c.id] : value.filter((x) => x !== c.id))} data-testid={`sav-col-${c.id}`} />{c.label}
            </label>
          ))}
        </div>
      )}
    </div>
  );
}

export default function SavFiltersBar({ opts, view, setView, allCols, onSaveAs, onUpdate }) {
  const f = view.filters || {};
  const set = (p) => setView({ ...view, filters: { ...f, ...p } });
  const mats = opts.fields.find((x) => x.id === "probleme_materiel")?.options || [];
  const types = [...(opts.fields.find((x) => x.id === "type_sav")?.options || []), DESINST];
  const active = Object.values(f).some((v) => v !== undefined && v !== "" && !(Array.isArray(v) && !v.length));
  return (
    <div className="space-y-2 mb-3" data-testid="sav-filters">
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative w-64"><Search className="w-3.5 h-3.5 absolute left-2 top-2 text-norea-textMuted" />
          <input value={f.q || ""} onChange={(e) => set({ q: e.target.value })} placeholder="Référence, client, ville…" className={`${sel} w-full pl-7`} data-testid="sav-search" /></div>
        <select value={f.type_sav || ""} onChange={(e) => set({ type_sav: e.target.value, status: [] })} className={sel} data-testid="sav-filter-type"><option value="">Tous types</option>{types.map((t) => <option key={t}>{t}</option>)}</select>
        <select value={f.probleme_materiel || ""} onChange={(e) => set({ probleme_materiel: e.target.value })} className={sel} data-testid="sav-filter-materiel"><option value="">Tout matériel</option>{mats.map((t) => <option key={t}>{t}</option>)}</select>
        <select value={f.subcontractor_id || ""} onChange={(e) => set({ subcontractor_id: e.target.value })} className={sel} data-testid="sav-filter-subcontractor">
          <option value="">Tous sous-traitants</option><option value="none">Sans sous-traitant</option>{opts.subcontractors.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}</select>
        <select value={f.employee_id || ""} onChange={(e) => set({ employee_id: e.target.value })} className={sel} data-testid="sav-filter-employee"><option value="">Tous salariés</option>{opts.employees.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}</select>
        <select value={f.rdv || ""} onChange={(e) => set({ rdv: e.target.value })} className={sel} data-testid="sav-filter-rdv"><option value="">RDV : tous</option><option value="upcoming">RDV à venir</option><option value="none">Sans RDV</option></select>
        <select value={f.relances ?? ""} onChange={(e) => set({ relances: e.target.value === "" ? "" : Number(e.target.value) })} className={sel} data-testid="sav-filter-relances">
          <option value="">Relances : toutes</option>{[0, 1, 2, 3].map((n) => <option key={n} value={n}>{n === 0 ? "Aucune relance" : `Relance ${n}`}</option>)}</select>
        {active && <button onClick={() => setView({ ...view, filters: {} })} className="btn-ghost text-xs" data-testid="sav-filters-clear"><X className="w-3.5 h-3.5" /> Effacer</button>}
        <div className="ml-auto flex items-center gap-2">
          <ColumnsPicker all={allCols} value={view.columns} onChange={(columns) => setView({ ...view, columns })} />
          {!view.system && view.id && <button onClick={onUpdate} className="btn-secondary text-xs" data-testid="sav-view-update"><Save className="w-3.5 h-3.5" /> Mettre à jour la vue</button>}
          <button onClick={onSaveAs} className="btn-secondary text-xs" data-testid="sav-view-save"><Save className="w-3.5 h-3.5" /> Enregistrer comme vue</button>
        </div>
      </div>
      <StatusChips statuses={viewStatuses(opts, f)} value={f.status} onChange={(status) => set({ status })} />
    </div>
  );
}
